import React, { useState, useEffect } from "react";
import styled from "styled-components";
// Components
import BlogBox from "../Elements/BlogBox";
import FullButton from "../Buttons/FullButton";
import AnimateOnScroll from "../AnimateOnScroll";
// Services
import { getBlogs } from "../../services/blogApi";

export default function Blog() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getBlogs({ skip: 0, limit: 6 });
        console.log('Blog component received response:', response);
        if (response && response.data && Array.isArray(response.data)) {
          setBlogs(response.data);
        } else {
          console.warn('Unexpected response format:', response);
          setError('Unexpected response format from API');
        }
      } catch (err) {
        console.error('Error fetching blogs:', err);
        setError(err.message || 'Failed to load blog posts');
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const getAuthorName = (createdBy) => {
    // If created_by is just an ID, return a default name
    // You might want to fetch author details separately if needed
    return 'AFRIQ Admin';
  };

  const getSlugFromId = (id) => {
    // Use the blog ID as the slug for routing
    return id || 'blog-post';
  };
  return (
    <Wrapper id="blog">
      <div className="whiteBg">
        <div className="container">
          <AnimateOnScroll animation="fadeUp" delay={0} duration={800}>
            <HeaderInfo>
              <h1 className="font40 extraBold">Our Latest Blog</h1>
              <p className="font15">
                Stay updated with the latest educational insights, study tips, and resources from AFRIQ Bamenda. 
                <br />
                Discover expert advice, strategies, and guidance to help you excel in your academic journey.
              </p>
            </HeaderInfo>
          </AnimateOnScroll>
          {loading ? (
            <LoadingContainer>
              <LoadingSpinner />
              <p>Loading blog posts...</p>
            </LoadingContainer>
          ) : error ? (
            <ErrorContainer>
              <p>Error: {error}</p>
              <p style={{ fontSize: '0.9rem', marginTop: '10px', opacity: 0.8 }}>
                Please check your API connection and try again.
              </p>
            </ErrorContainer>
          ) : blogs.length === 0 ? (
            <EmptyContainer>
              <p>No blog posts available at the moment.</p>
            </EmptyContainer>
          ) : (
            <>
              <div className="row textCenter">
                {blogs.slice(0, 3).map((blog, index) => (
                  <div key={blog.id} className="col-xs-12 col-sm-4 col-md-4 col-lg-4">
                    <AnimateOnScroll animation="fadeUp" delay={index * 200} duration={800}>
                      <BlogBox
                        img={blog.images && blog.images.length > 0 ? blog.images[0] : null}
                        title={blog.title}
                        text={blog.meta_description || blog.content?.replace(/<[^>]*>/g, '').substring(0, 150) + '...' || 'No description available'}
                        tag={blog.category || 'Blog'}
                        author={`${getAuthorName(blog.created_by)}, ${formatDate(blog.created_at)}`}
                        slug={getSlugFromId(blog.id)}
                      />
                    </AnimateOnScroll>
                  </div>
                ))}
              </div>
              {blogs.length > 3 && (
                <div className="row textCenter">
                  {blogs.slice(3, 6).map((blog, index) => (
                    <div key={blog.id} className="col-xs-12 col-sm-4 col-md-4 col-lg-4">
                      <AnimateOnScroll animation="fadeUp" delay={(index + 3) * 200} duration={800}>
                        <BlogBox
                          img={blog.images && blog.images.length > 0 ? blog.images[0] : null}
                          title={blog.title}
                          text={blog.meta_description || blog.content?.replace(/<[^>]*>/g, '').substring(0, 150) + '...' || 'No description available'}
                          tag={blog.category || 'Blog'}
                          author={`${getAuthorName(blog.created_by)}, ${formatDate(blog.created_at)}`}
                          slug={getSlugFromId(blog.id)}
                        />
                      </AnimateOnScroll>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          <div className="row flexCenter">
            <AnimateOnScroll animation="bounceIn" delay={1200} duration={800}>
              <div style={{ margin: "50px 0", width: "200px" }}>
                <FullButton title="View All Articles" to="/blogs" />
              </div>
            </AnimateOnScroll>
          </div>
        </div>
      </div>
    </Wrapper>
  );
}

const Wrapper = styled.section`
  width: 100%;
  padding: 120px 0;
  background: #f8f8ff;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
  
  @media (max-width: 480px) {
    padding: 60px 0;
  }
`;
const HeaderInfo = styled.div`
  margin-bottom: 50px;
  color: #000000;
  text-align: center;
  
  h1 {
    color: #4b2e83;
    font-size: clamp(1.75rem, 4vw, 2.5rem);
    margin-bottom: 20px;
    line-height: 1.3;
  }
  
  p {
    color: #000000;
    font-size: clamp(0.9rem, 2vw, 0.938rem);
    line-height: 1.7;
    max-width: 700px;
    margin: 0 auto;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 30px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
  
  p {
    margin-top: 20px;
    color: #4b2e83;
    font-size: 1rem;
  }
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid rgba(75, 46, 131, 0.1);
  border-top: 4px solid #4b2e83;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  padding: 60px 20px;
  text-align: center;
  background: #fff5f5;
  border: 2px solid #ff6b6b;
  border-radius: 12px;
  margin: 40px 0;
  
  p {
    color: #ff6b6b;
    font-size: 1rem;
    font-weight: 600;
  }
`;

const EmptyContainer = styled.div`
  padding: 80px 20px;
  text-align: center;
  
  p {
    color: #666;
    font-size: 1rem;
  }
`;